/// <amd-module name="Repos/PushesList/PushesDataProviderService" />
import { GitRepository } from "Repos/Common/Contracts/Git";
import { IReposRoutingService } from "Repos/Common/ReposRoutingService";
import { processGitStatusArtifactUris } from "Repos/Common/Util/Build";
import { getShortCommitId, isEmptyObjectId, versionStringToRefName } from "Repos/Common/Util/Ref";
import { IVCViewModelDataProviderService } from "Repos/Common/VCViewModelDataProviderService";
import { IGitPushEntry, IPush, IPushesDataProviderData } from "Repos/PushesList/Contracts";
import * as Resources from "Repos/PushesList/Resources";
import { format } from "VSS/Core/Util/String";
import { IIdentity } from "VSS/Features/Identities/IdentityService";
import { IVssService, Services, VssService } from "VSS/Platform/Context";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { IVssContributionService } from "VSS/Platform/Contribution";

export interface PushesSearchFilterData {
    allRefs?: boolean;
    excludeUsers?: string;
    fromDate?: Date;
    itemVersion?: string;
    toDate?: Date;
    userId?: string;
    userName?: string;
}

export interface IPushesDataProviderService extends IVssService {
    getBranchName(): string;
    getPushes(): IPush[];
    getPushedBy(): IIdentity | undefined;
    getExcludedFromIdentities(): IIdentity[] | undefined;
    fetchPushes(pushesSearchFilterData: PushesSearchFilterData, skip: number, top: number): Promise<IPush[]>;
}

interface IPushesSearchContext {
    repositoryName: string;
    searchCriteria: IPushesSearchCriteria;
    $skip?: number | undefined;
    $top?: number | undefined;
}

interface IPushesSearchCriteria {
    pusherId: string | undefined;
    refName: string | null | undefined;
    includeRefUpdates: boolean;
    excludePushers: string | undefined;
    fromDate: Date | undefined;
    toDate: Date | undefined;
}

class PushesDataProviderService extends VssService implements IPushesDataProviderService {
    private dataProviderKey: string = "ms.vss-code-web.pushes-data-provider";

    public getPushedBy(): IIdentity | undefined {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const data: IPushesDataProviderData | undefined = contributionService.getData<IPushesDataProviderData>(this.dataProviderKey);
        const pushedBy = data && data.pushedBy;
        let identity: IIdentity | undefined = undefined;
        if (pushedBy) {
            identity = this.convertIdentiyRefToIIdentity(pushedBy);
        }
        return identity;
    }

    public getExcludedFromIdentities(): IIdentity[] | undefined {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const data: IPushesDataProviderData | undefined = contributionService.getData<IPushesDataProviderData>(this.dataProviderKey);

        let identites: IIdentity[] | undefined = undefined;
        if (data && data.excludedFrom) {
            identites = data.excludedFrom.map(this.convertIdentiyRefToIIdentity);
        }

        return identites;
    }

    public getBranchName(): string {
        const vcViewModelDataProviderService = this.pageContext.getService<IVCViewModelDataProviderService>("IVCViewModelDataProviderService");
        return vcViewModelDataProviderService.getDefaultGitBranchName() || "";
    }

    public getPushes(): IPush[] {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const data: IPushesDataProviderData | undefined = contributionService.getData<IPushesDataProviderData>(this.dataProviderKey);
        const vcViewModelDataProviderService = this.pageContext.getService<IVCViewModelDataProviderService>("IVCViewModelDataProviderService");
        const repository = vcViewModelDataProviderService.getRepository();

        const pushes: IPush[] = [];
        if (data && data.pushes) {
            for (const pushEntry of data.pushes) {
                pushes.push(this.convertPush(repository, pushEntry));
            }
        }

        return pushes;
    }

    public async fetchPushes(pushesSearchFilterData: PushesSearchFilterData, skip: number, top: number): Promise<IPush[]> {
        const pushes: IPush[] = [];
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const vcViewModelDataProviderService = this.pageContext.getService<IVCViewModelDataProviderService>("IVCViewModelDataProviderService");
        const repository = vcViewModelDataProviderService.getRepository();

        if (repository) {
            const data = await contributionService.getDataAsync<IPushesDataProviderData>(
                this.dataProviderKey,
                this.getDataProviderProperties(repository.name, pushesSearchFilterData, skip, top),
                true
            );

            if (data && data.pushes) {
                for (const pushEntry of data.pushes) {
                    pushes.push(this.convertPush(repository, pushEntry));
                }
            }
        }
        return pushes;
    }

    private convertIdentiyRefToIIdentity(identityRef: IdentityRef): IIdentity {
        return {
            entityId: "",
            entityType: "",
            originId: identityRef.id,
            localId: identityRef.id,
            originDirectory: "",
            displayName: identityRef.displayName,
            image: identityRef.imageUrl
        };
    }

    private convertPush(repository: GitRepository | undefined, pushEntry: IGitPushEntry): IPush {
        const reposRoutingService = this.pageContext.getService<IReposRoutingService>("IReposRoutingService");
        let linkText: string;
        let linkHref: string;
        let commitId: string;

        if (repository) {
            linkText = this.getRefUpdateDescription(pushEntry);
            if (pushEntry.pullRequest) {
                linkHref = reposRoutingService.getPullRequestUrl(repository.name, repository.project.name, pushEntry.pullRequest.pullRequestId);
                commitId = pushEntry.push.refUpdates[0].newObjectId;
            } else {
                linkHref = reposRoutingService.getPushUrl(repository.name, repository.project.name, pushEntry.push.pushId);
                commitId = pushEntry.headCommit ? pushEntry.headCommit.commitId : "";
            }

            if (pushEntry.status) {
                pushEntry.status = processGitStatusArtifactUris(this.pageContext, repository.project.name, pushEntry.status);
            }
        } else {
            linkText = pushEntry.push.refUpdates[0].name;
            linkHref = "#";
            commitId = pushEntry.push.refUpdates[0].newObjectId;
        }

        return {
            pushId: pushEntry.push.pushId,
            pushedBy: pushEntry.push.pushedBy,
            linkProps: { text: linkText, href: linkHref },
            commitId: commitId,
            pushDate: pushEntry.push.date,
            childCount: 0,
            push: pushEntry
        };
    }

    private getRefUpdateDescription(push: IGitPushEntry): string {
        if (isEmptyObjectId(push.push.refUpdates[0].newObjectId)) {
            return Resources.PushRefDeletedShortFormat;
        }

        if (push.pullRequest) {
            return format(Resources.PullRequestLinkText, push.pullRequest.pullRequestId, push.pullRequest.title);
        } else {
            let refUpdateDescription = "";

            if (isEmptyObjectId(push.push.refUpdates[0].oldObjectId)) {
                refUpdateDescription = format(Resources.PushRefCreatedShortFormat, getShortCommitId(push.push.refUpdates[0].newObjectId));
            } else {
                refUpdateDescription = format(Resources.PushRefUpdateShortFormat, getShortCommitId(push.push.refUpdates[0].newObjectId));
            }
            let comment = "";
            if (push.headCommit) {
                comment = push.headCommit.comment;
            }
            return refUpdateDescription + ": " + comment;
        }
    }

    private getDataProviderProperties(repositoryName: string, filterData: PushesSearchFilterData, skip?: number, top?: number): IPushesSearchContext {
        const searchCriteria: IPushesSearchCriteria = {
            pusherId: filterData.userId,
            refName: filterData.allRefs ? null : versionStringToRefName(filterData.itemVersion),
            includeRefUpdates: true,
            excludePushers: filterData.excludeUsers,
            fromDate: undefined,
            toDate: undefined
        };

        if (filterData.toDate) {
            searchCriteria.toDate = filterData.toDate;
        }

        if (filterData.fromDate) {
            searchCriteria.fromDate = filterData.fromDate;
        }

        return {
            repositoryName: repositoryName,
            searchCriteria: searchCriteria,
            $skip: skip,
            $top: top
        };
    }
}

Services.add("IPushesDataProviderService", { serviceFactory: PushesDataProviderService });
